import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Table, Button, Switch, Typography, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import httpInjectorService from 'services/http-injector.service';
import {
  Plus, Pencil, Power, Search, RefreshCw, ChevronRight,
} from 'lucide-react';
import { Stamp, Modal, Field } from './SharedUI';

const { Title, Text } = Typography;

/* Adjust this to your existing money formatter */
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `₹${Number(value).toLocaleString()}`;
}

const StatusSwitch = ({ checked, onChange, disabled }) => {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '20px',
        border: 'none',
        padding: '3px',
        background: checked ? '#22c55e' : '#9ca3af',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: checked ? 'flex-end' : 'flex-start',
        transition: 'background 0.2s ease',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        style={{
          width: '18px',
          height: '18px',
          background: '#fff',
          borderRadius: '50%',
          display: 'block',
          transition: 'transform 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      />
    </button>
  );
};

export function PlansView({ notify, setPlansCache }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', plan }
  const [saving, setSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpInjectorService.getSubscriptionPlans();
      setPlans(res.plans || res.data || []);
    } catch (e) {
      notify?.(e.message || 'Could not load plans', 'error');
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPlansCache?.(plans); }, [plans]); // eslint-disable-line

  const submitPlan = async (form) => {
    setSaving(true);
    try {
      const fn = modal.mode === 'create' ? httpInjectorService.createPlan : httpInjectorService.updatePlan;
      await fn(form);
      notify?.(modal.mode === 'create' ? 'Plan created' : 'Plan updated');
      await load();
      setModal(null);
    } catch (e) {
      notify?.(e.message || 'Something went wrong', 'error');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (plan) => {
    const currentStatus = Number(plan.is_active) === 1;
    const nextStatus = currentStatus ? 0 : 1;

    setStatusUpdating(plan.id);

    try {
      await httpInjectorService.changePlanStatus({
        plan_id: Number(plan.id),
        is_active: nextStatus,
      });

      // Update UI immediately
      setPlans(prevPlans =>
        prevPlans.map(item =>
          item.id === plan.id
            ? { ...item, is_active: nextStatus }
            : item
        )
      );

      notify?.(
        nextStatus === 1
          ? `${plan.plan_name} activated`
          : `${plan.plan_name} deactivated`
      );
    } catch (e) {
      console.error('Status update error:', e);
      notify?.(
        e.message || 'Could not change plan status',
        'error'
      );
    } finally {
      setStatusUpdating(null);
    }
  };

  /* ── Table column definitions ─────────────────────────────────── */
  const columns = [
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
      sorter: (a, b) => String(a.plan_name || '').localeCompare(String(b.plan_name || '')),
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Price',
      key: 'price',
      width: 110,
      render: (_, plan) => (
        <span style={{ fontFamily: 'monospace' }}>
          {plan.plan_type === 'paid' ? fmtMoney(plan.price) : 'Free'}
        </span>
      ),
    },
    {
      title: 'Cycle',
      key: 'cycle',
      width: 140,
      render: (_, plan) => (
        <span style={{ textTransform: 'capitalize' }}>
          {plan.plan_type === 'paid'
            ? (plan.billing_cycle || '').replace('_', ' ')
            : `${plan.duration_value} ${plan.duration_type}`}
        </span>
      ),
    },
    {
      title: 'Features',
      key: 'features',
      render: (_, plan) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {plan.plan_type === 'paid'
            ? (plan.description || '—')
            : (plan.duration_label || `${plan.duration_value} ${plan.duration_type} trial`)}
        </Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_, plan) => {
        const isActive = Number(plan.is_active) === 1;
        return (
          <Space>
            <Switch
              size="small"
              checked={isActive}
              loading={statusUpdating === plan.id}
              onChange={() => toggleStatus(plan)}
            />
            <Tag color={isActive ? 'green' : 'default'}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      align: 'right',
      render: (_, plan) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => setModal({ mode: 'edit', plan })}
        />
      ),
    },
  ];

  return (
    <Card style={{ marginTop: '50px' }}>
      {/* Header Row */}
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            💳 Plans
          </Title>
          <Text type="secondary">Create and manage billing plans offered to organizations.</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModal({ mode: 'create', plan: null })}
          >
            New plan
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div style={{ position: 'relative', zIndex: 0 }}>
        <Table
          columns={columns}
          dataSource={plans}
          rowKey="id"
          bordered
          loading={loading}
          pagination={false}
          size="middle"
          style={{ marginTop: '20px' }}
        />
      </div>

      {modal && (
        <PlanModal
          mode={modal.mode}
          plan={modal.plan}
          saving={saving}
          onClose={() => setModal(null)}
          onSubmit={submitPlan}
        />
      )}
    </Card>
  );
}

const DURATION_TYPES = ['days', 'months', 'years'];
const BILLING_CYCLES = ['monthly', 'quarterly', 'half_yearly', 'yearly', 'one_time'];
const CURRENCIES = ['INR', 'USD'];

function PlanModal({ mode, plan, saving, onClose, onSubmit }) {
  const [form, setForm] = useState(() => plan
    ? {
        id: plan.id,
        plan_name: plan.plan_name,
        plan_type: plan.plan_type,
        description: plan.description || '',
        duration_type: plan.duration_type,
        duration_value: plan.duration_value,
        duration_label: plan.duration_label || '',
        price: plan.price || '',
        currency: plan.currency || 'INR',
        billing_cycle: plan.billing_cycle || 'monthly',
      }
    : {
        plan_name: '',
        plan_type: 'free_trial',
        description: '',
        duration_type: 'days',
        duration_value: '',
        duration_label: '',
        price: '',
        currency: 'INR',
        billing_cycle: 'monthly',
      });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isPaid = form.plan_type === 'paid';

  const canSubmit = form.plan_name && form.plan_type && form.duration_type && form.duration_value
    && (!isPaid || (form.price && form.currency && form.billing_cycle));

  const buildPayload = () => {
    const base = {
      plan_name: form.plan_name,
      plan_type: form.plan_type,
      duration_type: form.duration_type,
      duration_value: Number(form.duration_value),
    };
    if (!isPaid) return base;
    return {
      ...base,
      description: form.description,
      duration_label: form.duration_label,
      price: Number(form.price),
      currency: form.currency,
      billing_cycle: form.billing_cycle,
    };
  };

  return (
    <Modal title={mode === 'create' ? 'Create plan' : `Edit ${plan.plan_name}`} onClose={onClose}>
      <Field label="Plan name">
        <input
          className="sa-input w-full px-3 py-2 text-sm"
          value={form.plan_name}
          onChange={e => set('plan_name', e.target.value)}
          placeholder="e.g. Starter Plan 6 Month"
        />
      </Field>

      <Field label="Plan type">
        <select
          className="sa-input w-full px-3 py-2 text-sm"
          value={form.plan_type}
          onChange={e => set('plan_type', e.target.value)}
        >
          <option value="free_trial">Free trial</option>
          <option value="paid">Paid</option>
        </select>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Duration type">
          <select
            className="sa-input w-full px-3 py-2 text-sm"
            value={form.duration_type}
            onChange={e => set('duration_type', e.target.value)}
          >
            {DURATION_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Duration value">
          <input
            type="number"
            className="sa-input w-full px-3 py-2 text-sm sa-mono"
            value={form.duration_value}
            onChange={e => set('duration_value', e.target.value)}
            placeholder="6"
          />
        </Field>
      </div>

      {isPaid && (
        <>
          <Field label="Description">
            <input
              className="sa-input w-full px-3 py-2 text-sm"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Starter paid subscription"
            />
          </Field>

          <Field label="Duration label">
            <input
              className="sa-input w-full px-3 py-2 text-sm"
              value={form.duration_label}
              onChange={e => set('duration_label', e.target.value)}
              placeholder="6 Month"
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Price">
              <input
                type="number"
                className="sa-input w-full px-3 py-2 text-sm sa-mono"
                value={form.price}
                onChange={e => set('price', e.target.value)}
                placeholder="4999"
              />
            </Field>
            <Field label="Currency">
              <select
                className="sa-input w-full px-3 py-2 text-sm"
                value={form.currency}
                onChange={e => set('currency', e.target.value)}
              >
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Billing cycle">
              <select
                className="sa-input w-full px-3 py-2 text-sm"
                value={form.billing_cycle}
                onChange={e => set('billing_cycle', e.target.value)}
              >
                {BILLING_CYCLES.map(b => <option key={b} value={b}>{b.replace('_', ' ')}</option>)}
              </select>
            </Field>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 mt-2">
        <button className="sa-btn sa-btn-ghost px-4 py-2 text-sm" onClick={onClose}>Cancel</button>
        <button
  className="sa-btn sa-btn-primary px-4 py-2 text-sm"
  disabled={saving || !canSubmit}
  onClick={() => { console.log('button clicked, payload:', buildPayload()); onSubmit(buildPayload()); }}
>
          {saving ? 'Saving…' : mode === 'create' ? 'Create plan' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}